import React, { useEffect, useMemo, useState } from 'react';
import * as RDialog from '@radix-ui/react-dialog';
import { Modal, Button, Field, Input } from '../SupportCoordination/kit';
import clsx from 'clsx';
import * as RMenu from '@radix-ui/react-dropdown-menu';
import {
  PenSquare, EyeOff, Ban, Trash2, Archive, ShieldAlert, Reply, ReplyAll, Forward, Brush, FolderInput, ListFilter, Zap, MailOpen, Tag, Flag,
  Pin, AlarmClockOff, Printer, Undo2, Settings, MessagesSquare, AlignLeft, ChevronsDownUp, ZoomIn, ZoomOut, RefreshCw, LayoutPanelTop,
  PanelLeft, PanelRight, AlignJustify, BookOpen, ChevronDown, Check, ShieldCheck, Clock, Folder, X, Volume2, Square, Minus, Plus, CalendarPlus, Sun,
} from 'lucide-react';

// The Outlook-style ribbon above the list and the reading pane: Home (acting on mail) and View
// (how mail looks). Each button acts on the ticked messages, or else the open one. "Simple" folds
// the ribbon into one line of icons.
const itemCls = 'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] text-slate-700 cursor-pointer outline-none select-none data-[highlighted]:bg-slate-100 data-[disabled]:opacity-40';

const Big = React.forwardRef(function Big({ icon: Icon, label, onClick, disabled, active, danger, simple, title, caret, ...rest }, ref) {
  return (
    <button ref={ref} type="button" {...rest} onClick={onClick} disabled={disabled} title={title || label} aria-label={label} aria-pressed={active || undefined}
      className={clsx('flex-shrink-0 h-8 inline-flex items-center gap-1.5 rounded-md text-[13px] whitespace-nowrap transition-colors disabled:opacity-35 disabled:cursor-not-allowed data-[state=open]:bg-slate-100',
        simple ? 'px-1.5 min-w-[32px] justify-center' : 'px-2',
        active ? 'bg-sky-100 text-[#0f6cbd]' : danger ? 'text-slate-700 hover:bg-rose-50 hover:text-rose-700' : 'text-slate-700 hover:bg-slate-100')}>
      <Icon size={16} className={danger ? '' : 'text-[#0f6cbd]'} aria-hidden />
      {!simple && <span>{label}</span>}
      {caret && <ChevronDown size={12} className="-ml-0.5 opacity-60" aria-hidden />}
    </button>
  );
});
function Menu({ icon, label, items, disabled, simple, title }) {
  return (
    <RMenu.Root>
      <RMenu.Trigger asChild disabled={disabled}>
        <Big icon={icon} label={label} caret disabled={disabled} simple={simple} title={title} />
      </RMenu.Trigger>
      <RMenu.Portal>
        <RMenu.Content align="start" sideOffset={4} className="z-[80] min-w-[220px] max-h-[380px] overflow-y-auto mail-scroll bg-white rounded-lg shadow-lg ring-1 ring-slate-200 p-1">
          {items.filter(Boolean).map((it, i) => (it === 'sep' ? <RMenu.Separator key={`s${i}`} className="my-1 h-px bg-slate-100" />
            : it.label && it.header ? <RMenu.Label key={`h${i}`} className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{it.label}</RMenu.Label>
              : (
                <RMenu.Item key={`${it.label}${i}`} disabled={it.disabled} onSelect={it.onClick} className={clsx(itemCls, it.danger && 'text-rose-700')}>
                  {it.icon ? <it.icon size={14} className="text-slate-500" aria-hidden /> : it.dot ? <span className="w-3 h-3 rounded-full" style={{ background: it.dot }} aria-hidden /> : null}
                  <span className="flex-1">{it.label}</span>
                  {it.checked && <Check size={14} className="text-[#0f6cbd]" aria-hidden />}
                  {it.hint && <span className="text-[11.5px] text-slate-400">{it.hint}</span>}
                </RMenu.Item>
              )))}
        </RMenu.Content>
      </RMenu.Portal>
    </RMenu.Root>
  );
}
// A group of related buttons; a thin line between groups, as in Outlook's ribbon.
function Group({ label, children }) {
  return <div role="group" aria-label={label} className="flex items-center gap-0.5 pr-1.5 mr-1.5 border-r border-slate-200 last:border-0 last:mr-0">{children}</div>;
}

// Snooze choices, Outlook's.
const at = (days, hour) => { const d = new Date(); d.setDate(d.getDate() + days); d.setHours(hour, 0, 0, 0); return d; };
const nextDow = (dow, hour) => { const d = new Date(); d.setDate(d.getDate() + ((dow + 7 - d.getDay()) % 7 || 7)); d.setHours(hour, 0, 0, 0); return d; };
const fmt = (d) => d.toLocaleString('en-AU', { weekday: 'short', hour: 'numeric', minute: '2-digit' });

export default function Ribbon({
  prefs, savePrefs, has, readOnly, msg, tags, accountFolders, quickSteps, undoLabel, threadOpen, hasThread,
  on, // { new, ignore, block, blockDomain, trust, del, archive, report, reply, replyAll, forward, sweep, move, rules, newRule, quickStep, manageQuickSteps, read, unread, tag, manageTags, flag, unflag, pin, unpin, snooze, print, undo, settings, collapse, zoom, sync, immersive }
}) {
  const [tab, setTab] = useState('home');
  const simple = prefs.appearance.ribbon === 'simple';
  const ap = prefs.appearance;
  const setAp = (patch) => savePrefs({ appearance: { ...ap, ...patch } });
  const none = !has;
  const act = none || readOnly;
  const snoozeItems = [
    { label: 'Later today', hint: fmt(new Date(Date.now() + 3 * 3600000)), onClick: () => on.snooze(new Date(Date.now() + 3 * 3600000)) },
    { label: 'Tomorrow', hint: fmt(at(1, 8)), onClick: () => on.snooze(at(1, 8)) },
    { label: 'This weekend', hint: fmt(nextDow(6, 9)), onClick: () => on.snooze(nextDow(6, 9)) },
    { label: 'Next week', hint: fmt(nextDow(1, 8)), onClick: () => on.snooze(nextDow(1, 8)) },
    'sep',
    { label: 'Choose a date and time…', icon: Clock, onClick: () => on.snooze('pick') },
  ];
  return (
    <div className="flex-shrink-0 border-b border-slate-200 bg-white">
      <div className="flex items-center gap-1 px-2 pt-1">
        {[['home', 'Home'], ['view', 'View']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} aria-pressed={tab === k}
            className={clsx('px-3 h-8 text-[13px] rounded-t-md border-b-2', tab === k ? 'border-[#0f6cbd] text-slate-900 font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900')}>{l}</button>
        ))}
        <span className="flex-1" />
        <button type="button" onClick={() => setAp({ ribbon: simple ? 'classic' : 'simple' })} title={simple ? 'Classic ribbon' : 'Simple ribbon'}
          className="h-7 px-2 rounded-md text-[12px] text-slate-500 hover:bg-slate-100 inline-flex items-center gap-1"><LayoutPanelTop size={13} aria-hidden />{simple ? 'Classic' : 'Simple'}</button>
      </div>
      <div className="flex flex-wrap items-center gap-y-1 px-2 pb-1.5 pt-1">
        {tab === 'home' ? (
          <>
            <Group label="New" simple={simple}><Big icon={PenSquare} label="New" onClick={on.new} simple={simple} /></Group>
            <Group label="Delete" simple={simple}>
              <Big icon={EyeOff} label="Ignore" onClick={on.ignore} disabled={act || !msg} simple={simple} title="Ignore this conversation: it and new replies go to Deleted" />
              <Menu icon={Ban} label="Block" disabled={none || !msg} simple={simple} items={[
                { label: 'Block sender', icon: Ban, onClick: on.block, disabled: readOnly },
                { label: 'Block everyone at this domain', icon: Ban, onClick: on.blockDomain, disabled: readOnly },
                'sep',
                { label: 'Never block: safe sender', icon: ShieldCheck, onClick: on.trust },
              ]} />
              <Big icon={Trash2} label="Delete" onClick={on.del} disabled={act} danger simple={simple} />
              <Big icon={Archive} label="Archive" onClick={on.archive} disabled={act} simple={simple} />
            </Group>
            <Group label="Report" simple={simple}>
              <Menu icon={ShieldAlert} label="Report" disabled={act} simple={simple} items={[
                { label: 'Report junk', icon: ShieldAlert, onClick: () => on.report('junk') },
                { label: 'Report phishing', icon: ShieldAlert, onClick: () => on.report('phishing') },
              ]} />
            </Group>
            <Group label="Respond" simple={simple}>
              {/* Settings › Replies › Main reply button decides which comes first. */}
              {prefs.replies && prefs.replies.defaultReply === 'replyAll' ? (
                <><Big icon={ReplyAll} label="Reply all" onClick={on.replyAll} disabled={!msg} simple={simple} /><Big icon={Reply} label="Reply" onClick={on.reply} disabled={!msg} simple={simple} /></>
              ) : (
                <><Big icon={Reply} label="Reply" onClick={on.reply} disabled={!msg} simple={simple} /><Big icon={ReplyAll} label="Reply all" onClick={on.replyAll} disabled={!msg} simple={simple} /></>
              )}
              <Big icon={Forward} label="Forward" onClick={on.forward} disabled={!msg} simple={simple} />
              <Big icon={CalendarPlus} label="Meeting" onClick={on.meeting} disabled={!msg || readOnly} simple={simple} title="Book a meeting with the people on this email" />
            </Group>
            <Group label="Move" simple={simple}>
              <Menu icon={Brush} label="Sweep" disabled={act || !msg} simple={simple} title="Sweep: everything from this sender in the Inbox" items={[
                { header: true, label: 'From this sender, in the Inbox' },
                { label: 'Delete all', onClick: () => on.sweep({ mode: 'all', action: 'delete' }) },
                { label: 'Delete all but the newest', onClick: () => on.sweep({ mode: 'keep_latest', action: 'delete' }) },
                { label: 'Delete those older than 10 days', onClick: () => on.sweep({ mode: 'older_10', action: 'delete' }) },
                { label: 'Archive all', onClick: () => on.sweep({ mode: 'all', action: 'archive' }) },
                'sep',
                { label: 'Delete all, and always from now on', onClick: () => on.sweep({ mode: 'all', action: 'delete', always: true }) },
              ]} />
              <Menu icon={FolderInput} label="Move" disabled={act} simple={simple} items={[
                { header: true, label: 'Move to' },
                ...(accountFolders || []).map((f) => ({ label: f.path && f.path !== f.name ? f.path : f.name, icon: Folder, onClick: () => on.move(f) })),
              ]} />
              <Menu icon={ListFilter} label="Rules" simple={simple} items={[
                { label: msg ? 'Make a rule from this message…' : 'New rule…', icon: ListFilter, onClick: on.newRule },
                { label: 'Manage rules', icon: Settings, onClick: on.rules },
              ]} />
            </Group>
            <Group label="Quick steps" simple={simple}>
              <Menu icon={Zap} label="Quick steps" disabled={none} simple={simple} items={[
                ...(quickSteps || []).map((s) => ({ label: s.name, icon: Zap, onClick: () => on.quickStep(s), disabled: readOnly })),
                (quickSteps || []).length ? 'sep' : null,
                { label: 'New quick step…', icon: Zap, onClick: on.manageQuickSteps },
              ]} />
            </Group>
            <Group label="Tags" simple={simple}>
              <Menu icon={MailOpen} label="Read / Unread" disabled={act} simple={simple} items={[
                { label: 'Mark as read', icon: MailOpen, onClick: on.read }, { label: 'Mark as unread', icon: MailOpen, onClick: on.unread },
              ]} />
              <Menu icon={Tag} label="Tag" disabled={none} simple={simple} items={[
                ...(tags || []).map((t) => ({ label: t.name, dot: t.color, checked: msg && (msg.tag_ids || []).includes(t.id), onClick: () => on.tag(t) })),
                (tags || []).length ? 'sep' : null,
                { label: 'Make and change tags', icon: Tag, onClick: on.manageTags },
              ]} />
              <Menu icon={Flag} label="Flag" disabled={act} simple={simple} items={[
                { label: 'Flag', icon: Flag, onClick: on.flag }, { label: 'Clear flag', icon: Flag, onClick: on.unflag },
              ]} />
              <Big icon={Pin} label={msg && msg.pinned_at ? 'Unpin' : 'Pin'} onClick={msg && msg.pinned_at ? on.unpin : on.pin} disabled={act} active={!!(msg && msg.pinned_at)} simple={simple} />
              <Menu icon={AlarmClockOff} label="Snooze" disabled={act} simple={simple} items={snoozeItems} />
            </Group>
            <Group label="Print" simple={simple}><Big icon={Printer} label="Print" onClick={on.print} disabled={!msg} simple={simple} /></Group>
            <Group label="Undo" simple={simple}><Big icon={Undo2} label="Undo" onClick={on.undo} disabled={!undoLabel} simple={simple} title={undoLabel ? `Undo: ${undoLabel}` : 'Nothing to undo'} /></Group>
          </>
        ) : (
          <>
            <Group label="Settings" simple={simple}><Big icon={Settings} label="View settings" onClick={on.settings} simple={simple} /></Group>
            <Group label="Messages" simple={simple}>
              <Big icon={MessagesSquare} label="Conversations" onClick={() => savePrefs({ conversations: !prefs.conversations })} active={prefs.conversations} simple={simple} />
              <Menu icon={AlignLeft} label="Message preview" simple={simple} items={[[0, 'Off'], [1, 'One line'], [2, 'Two lines']].map(([v, l]) => ({ label: l, checked: ap.preview === v, onClick: () => setAp({ preview: v }) }))} />
              <Big icon={ChevronsDownUp} label={threadOpen ? 'Collapse conversation' : 'Expand conversation'} onClick={on.collapse} disabled={!msg || !hasThread} simple={simple} />
              <Big icon={ZoomOut} label="Zoom out" onClick={() => on.zoom(-1)} disabled={(ap.zoom || 100) <= 80} simple={simple} />
              <span className="px-1 text-[12px] tabular-nums text-slate-500" aria-live="polite">{ap.zoom || 100}%</span>
              <Big icon={ZoomIn} label="Zoom in" onClick={() => on.zoom(1)} disabled={(ap.zoom || 100) >= 150} simple={simple} />
              <Big icon={RefreshCw} label="Sync" onClick={on.sync} simple={simple} />
            </Group>
            <Group label="Layout" simple={simple}>
              <Big icon={PanelLeft} label="Folder pane" onClick={() => setAp({ folderPane: !ap.folderPane })} active={ap.folderPane !== false} simple={simple} />
              <Menu icon={PanelRight} label="Reading pane" simple={simple} items={[['right', 'Show on the right'], ['bottom', 'Show at the bottom'], ['off', 'Hide']].map(([v, l]) => ({ label: l, checked: ap.readingPane === v, onClick: () => setAp({ readingPane: v }) }))} />
              <Big icon={Sun} label="My day" onClick={() => setAp({ myDay: !ap.myDay })} active={!!ap.myDay} simple={simple} title="Today's appointments and tasks beside your mail" />
              <Menu icon={AlignJustify} label="Density" simple={simple} items={[['roomy', 'Roomy'], ['compact', 'Compact']].map(([v, l]) => ({ label: l, checked: ap.density === v, onClick: () => setAp({ density: v }) }))} />
            </Group>
            <Group label="Immersive reader" simple={simple}><Big icon={BookOpen} label="Immersive reader" onClick={on.immersive} disabled={!msg} simple={simple} /></Group>
          </>
        )}
      </div>
    </div>
  );
}

// Snooze until a time the person picks.
const localNow = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
export function SnoozeDialog({ open, onClose, onPick }) {
  const [v, setV] = useState('');
  useEffect(() => { if (open) setV(localNow(new Date(Date.now() + 86400000))); }, [open]);
  const ok = v && new Date(v).getTime() > Date.now();
  return (
    <Modal open={open} onClose={onClose} title="Snooze until" sub="The mail leaves your list and comes back to the top, unread, at this time." width="sm"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={!ok} onClick={() => onPick(new Date(v))}>Snooze</Button></>}>
      <Field label="Date and time"><Input type="datetime-local" value={v} min={localNow(new Date())} onChange={(e) => setV(e.target.value)} /></Field>
    </Modal>
  );
}

// Immersive reader: the message alone, in big clear type, with read-aloud.
const NL = '\n';
const PARAS = /\n{2,}/;
const tidy = (t) => t.replace(/\u00a0/g, ' ').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
const THEMES = { light: ['#ffffff', '#1f2328'], sepia: ['#f6efe2', '#3b2f22'], dark: ['#1d2025', '#e8e6e3'] };
export function ImmersiveReader({ open, onClose, message }) {
  const [size, setSize] = useState(22);
  const [gap, setGap] = useState(1.8);
  const [theme, setTheme] = useState('light');
  const [speaking, setSpeaking] = useState(false);
  const text = useMemo(() => {
    if (!message) return '';
    if (message.html) {
      try {
        const d = new DOMParser().parseFromString(message.html, 'text/html');
        d.querySelectorAll('style,script,head,blockquote').forEach((n) => n.remove());
        d.querySelectorAll('br').forEach((n) => n.replaceWith(NL));
        d.querySelectorAll('p,div,li,tr,h1,h2,h3,h4').forEach((n) => n.append(NL));
        return tidy(d.body.textContent || '');
      } catch { /* fall back to the plain text */ }
    }
    return String(message.text || '').trim();
  }, [message]);
  const speech = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const stop = () => { if (speech) window.speechSynthesis.cancel(); setSpeaking(false); };
  useEffect(() => () => { if (speech) window.speechSynthesis.cancel(); }, [speech]);
  useEffect(() => { if (!open) stop(); }, [open]); // stop reading when it closes
  const speak = () => {
    if (!speech) return;
    if (speaking) { stop(); return; }
    const u = new SpeechSynthesisUtterance(`${message.subject || ''}. ${text}`);
    u.lang = 'en-AU'; u.onend = () => setSpeaking(false); u.onerror = () => setSpeaking(false);
    window.speechSynthesis.cancel(); window.speechSynthesis.speak(u); setSpeaking(true);
  };
  if (!message) return null;
  const [bg, fg] = THEMES[theme];
  const from = ((message.from || [])[0]) || {};
  const tool = 'h-9 px-2.5 rounded-md inline-flex items-center gap-1.5 text-[13px] hover:bg-black/5';
  return (
    <RDialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <RDialog.Portal>
        <RDialog.Content aria-describedby={undefined} className="fixed inset-0 z-[90] flex flex-col outline-none" style={{ background: bg, color: fg }}>
          <RDialog.Title className="sr-only">Immersive reader</RDialog.Title>
          <div className="flex-shrink-0 flex flex-wrap items-center gap-1 px-3 py-2 border-b" style={{ borderColor: theme === 'dark' ? '#33373d' : '#e5e2dc' }}>
            {speech && <button type="button" className={tool} onClick={speak}>{speaking ? <Square size={15} aria-hidden /> : <Volume2 size={15} aria-hidden />}{speaking ? 'Stop' : 'Read aloud'}</button>}
            <span className="mx-1 h-5 w-px bg-current opacity-20" aria-hidden />
            <button type="button" className={tool} onClick={() => setSize((x) => Math.max(16, x - 2))} aria-label="Smaller text"><Minus size={15} aria-hidden /></button>
            <span className="text-[13px] tabular-nums w-10 text-center">{size}px</span>
            <button type="button" className={tool} onClick={() => setSize((x) => Math.min(40, x + 2))} aria-label="Bigger text"><Plus size={15} aria-hidden /></button>
            <button type="button" className={tool} onClick={() => setGap((g) => (g >= 2.2 ? 1.5 : Math.round((g + 0.35) * 100) / 100))}>Line spacing</button>
            <span className="mx-1 h-5 w-px bg-current opacity-20" aria-hidden />
            {Object.entries(THEMES).map(([k, [b, f]]) => (
              <button key={k} type="button" onClick={() => setTheme(k)} aria-pressed={theme === k} aria-label={`${k} page`} title={k[0].toUpperCase() + k.slice(1)}
                className={'w-7 h-7 rounded-full ring-1 ring-black/15 ' + (theme === k ? 'outline outline-2 outline-offset-2 outline-[#0f6cbd]' : '')} style={{ background: b, color: f }}>A</button>
            ))}
            <span className="flex-1" />
            <button type="button" className={tool} onClick={onClose} aria-label="Close the immersive reader"><X size={17} aria-hidden /> Close</button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <article className="max-w-[68ch] mx-auto px-6 py-10" style={{ fontSize: size, lineHeight: gap, fontFamily: 'Georgia, "Iowan Old Style", "Times New Roman", serif' }}>
              <h1 className="font-semibold mb-2" style={{ fontSize: Math.round(size * 1.35), lineHeight: 1.25 }}>{message.subject || '(no subject)'}</h1>
              <p className="mb-8 opacity-70" style={{ fontSize: Math.round(size * 0.7) }}>{from.name || from.address}</p>
              {text.split(PARAS).map((para, i) => <p key={i} className="mb-[1em] whitespace-pre-line">{para}</p>)}
            </article>
          </div>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
